import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
    Alert,
    Box,
    Button,
    Chip,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    Link,
    Stack,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    TextField,
    Typography,
} from "@mui/material";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import CancelOutlinedIcon from "@mui/icons-material/CancelOutlined";
import OpenInNewOutlinedIcon from "@mui/icons-material/OpenInNewOutlined";
import Popup from "../../../components/Popup";
import { resolveUploadedMediaUrl } from "../../../config/api";
import { getSchoolIdForUser } from "../../../utils/userContext";
import {
    approveFeeSubmission,
    getPendingFeeSubmissions,
    rejectFeeSubmission,
} from "../../../redux/feeRelated/feeHandle";
import {
    FinancePageContainer,
    FinancePageHeader,
    FinanceHeadCell,
    FinanceTableRow,
    financeTableContainerSx,
} from "./financeUi";

const formatMWK = (n) => `MWK ${Number(n || 0).toLocaleString()}`;

function termLabel(row) {
    const rec = row?.feeRecord;
    if (!rec) return "—";
    const parts = [rec.term, rec.academicYear].filter(Boolean);
    return parts.length ? parts.join(" · ") : "School fees";
}

const FeePaymentSubmissions = () => {
    const dispatch = useDispatch();
    const { currentUser } = useSelector((s) => s.user);
    const { feePaymentSubmissions, loading } = useSelector((s) => s.fee);
    const schoolId = getSchoolIdForUser(currentUser);

    const [showPopup, setShowPopup] = useState(false);
    const [message, setMessage] = useState("");
    const [rejectOpen, setRejectOpen] = useState(false);
    const [rejectTarget, setRejectTarget] = useState(null);
    const [rejectReason, setRejectReason] = useState("");

    const load = useCallback(() => {
        if (schoolId) dispatch(getPendingFeeSubmissions(schoolId));
    }, [dispatch, schoolId]);

    useEffect(() => {
        load();
    }, [load]);

    const pending = useMemo(
        () => (Array.isArray(feePaymentSubmissions) ? feePaymentSubmissions : []),
        [feePaymentSubmissions],
    );

    const handleApprove = async (row) => {
        const id = row.id || row._id;
        const result = await dispatch(
            approveFeeSubmission(id, { reviewedById: currentUser?._id || currentUser?.id }),
        );
        if (result?.ok === false) {
            setMessage(result.message || "Could not approve payment.");
            setShowPopup(true);
            return;
        }
        setMessage(
            result?.data?.message || "Payment verified and recorded on the student account.",
        );
        setShowPopup(true);
        load();
    };

    const openReject = (row) => {
        setRejectTarget(row);
        setRejectReason("");
        setRejectOpen(true);
    };

    const handleReject = async () => {
        if (!rejectTarget) return;
        const id = rejectTarget.id || rejectTarget._id;
        const result = await dispatch(
            rejectFeeSubmission(id, {
                reviewedById: currentUser?._id || currentUser?.id,
                rejectionReason: rejectReason.trim(),
            }),
        );
        setRejectOpen(false);
        if (result?.ok === false) {
            setMessage(result.message || "Could not reject submission.");
        } else {
            setMessage("Payment submission rejected. The student can submit again.");
        }
        setShowPopup(true);
        load();
    };

    return (
        <FinancePageContainer maxWidth="lg">
            <Popup message={message} setShowPopup={setShowPopup} showPopup={showPopup} />

            <FinancePageHeader
                title="Verify student payments"
                subtitle="Students upload proof from the portal. Confirm cash-in, bank transfer, and the fee balance before approving — proof may be invalid."
                action={
                    <Button variant="outlined" onClick={load} disabled={loading}>
                        Refresh
                    </Button>
                }
            />

            <Alert severity="warning" sx={{ mb: 2, borderRadius: 2 }}>
                <Typography variant="subtitle2" fontWeight={700} gutterBottom>
                    Before you approve
                </Typography>
                <Typography variant="body2" component="ul" sx={{ m: 0, pl: 2.5 }}>
                    <li>Confirm the money actually arrived in the school account (cash-in or transfer).</li>
                    <li>Match the amount and reference against the student&apos;s outstanding balance.</li>
                    <li>Reject if the screenshot looks edited, duplicated, or does not match your records.</li>
                </Typography>
            </Alert>

            {pending.length === 0 ? (
                <Alert severity="success" sx={{ borderRadius: 2 }}>
                    No student payments waiting for verification.
                </Alert>
            ) : (
                <TableContainer sx={financeTableContainerSx}>
                    <Table size="medium">
                        <TableHead>
                            <TableRow>
                                <FinanceHeadCell>Student</FinanceHeadCell>
                                <FinanceHeadCell>Statement</FinanceHeadCell>
                                <FinanceHeadCell align="right">Amount</FinanceHeadCell>
                                <FinanceHeadCell align="right">Balance owed</FinanceHeadCell>
                                <FinanceHeadCell>Method</FinanceHeadCell>
                                <FinanceHeadCell>Reference</FinanceHeadCell>
                                <FinanceHeadCell>Proof</FinanceHeadCell>
                                <FinanceHeadCell align="right">Actions</FinanceHeadCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {pending.map((row) => {
                                const id = row.id || row._id;
                                const proofUrl = resolveUploadedMediaUrl(row.proofUrl);
                                return (
                                    <FinanceTableRow key={id}>
                                        <TableCell sx={{ fontWeight: 600 }}>
                                            {row.student?.name || "Student"}
                                            {row.student?.rollNum != null ? (
                                                <Typography variant="caption" color="text.secondary" display="block">
                                                    Roll {row.student.rollNum}
                                                </Typography>
                                            ) : null}
                                        </TableCell>
                                        <TableCell>{termLabel(row)}</TableCell>
                                        <TableCell align="right" sx={{ fontWeight: 700 }}>
                                            {formatMWK(row.amount)}
                                        </TableCell>
                                        <TableCell align="right">
                                            {formatMWK(row.feeRecord?.balance)}
                                        </TableCell>
                                        <TableCell>{row.paymentMethod || "—"}</TableCell>
                                        <TableCell>{row.referenceNumber || "—"}</TableCell>
                                        <TableCell>
                                            {proofUrl ? (
                                                <Link href={proofUrl} target="_blank" rel="noopener noreferrer">
                                                    View proof <OpenInNewOutlinedIcon sx={{ fontSize: 14, verticalAlign: "middle" }} />
                                                </Link>
                                            ) : (
                                                "—"
                                            )}
                                        </TableCell>
                                        <TableCell align="right">
                                            <Stack direction="row" spacing={1} justifyContent="flex-end">
                                                <Button
                                                    size="small"
                                                    variant="contained"
                                                    color="success"
                                                    startIcon={<CheckCircleOutlineIcon />}
                                                    onClick={() => handleApprove(row)}
                                                >
                                                    Approve
                                                </Button>
                                                <Button
                                                    size="small"
                                                    variant="outlined"
                                                    color="error"
                                                    startIcon={<CancelOutlinedIcon />}
                                                    onClick={() => openReject(row)}
                                                >
                                                    Reject
                                                </Button>
                                            </Stack>
                                        </TableCell>
                                    </FinanceTableRow>
                                );
                            })}
                        </TableBody>
                    </Table>
                </TableContainer>
            )}

            <Box sx={{ mt: 2 }}>
                <Chip label={`${pending.length} pending`} color={pending.length ? "warning" : "success"} />
            </Box>

            <Dialog open={rejectOpen} onClose={() => setRejectOpen(false)} maxWidth="xs" fullWidth>
                <DialogTitle>Reject payment submission</DialogTitle>
                <DialogContent dividers>
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                        Tell the student why this proof could not be accepted. They can submit again with a corrected
                        payment.
                    </Typography>
                    <TextField
                        fullWidth
                        multiline
                        minRows={3}
                        label="Reason"
                        value={rejectReason}
                        onChange={(e) => setRejectReason(e.target.value)}
                    />
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setRejectOpen(false)}>Cancel</Button>
                    <Button color="error" variant="contained" onClick={handleReject}>
                        Reject
                    </Button>
                </DialogActions>
            </Dialog>
        </FinancePageContainer>
    );
};

export default FeePaymentSubmissions;
